import type { Priority, Stage } from '../../api/types'
import { PRIORITY_LABEL, STAGE_LABEL } from '../pipeline/labels'
import styles from './DealPanel.module.css'

/**
 * Where the opportunity currently sits — not why.
 *
 * Stage, priority and commercial progress answer "how far along is this", which
 * is a different question from "what do we actually know". The panel used to
 * blur them: the five progression signals sat in the same chip row as the seven
 * qualification signals, so `pricing discussed` (a step in a process) looked
 * exactly like `problem confirmed` (a fact a call established).
 *
 * **Progression signals are not shown at all.** They were briefly listed here as
 * "commercial progress" — the reasoning being that they are workflow rather than
 * evidence, so they belonged with stage. That reasoning holds, and the conclusion
 * was still wrong: stage already summarises how far the motion has travelled, so
 * repeating its inputs beside it says the same thing twice at a place in the
 * panel that exists to be quiet. Stage and priority are the only deal metadata
 * that earn a line here (Mike, 2026-08-12).
 *
 * Rendered as a definition list, deliberately typographic: no chips, no colour,
 * no borders. The visual separation carries the meaning, so a reader can tell
 * evidence from process state before reading a single word.
 *
 * It sits last in the reasoning flow. The panel answers what is confirmed, what
 * is missing and what follows from that; only then, where the deal sits.
 */
export function WorkflowContext({ stage, priority }: { stage: Stage | null; priority: Priority | null }) {
  // Nothing known yet: an unanalysed deal has no position to report, and
  // "Not set · Not set" is noise pretending to be information.
  if (!stage && !priority) return null

  return (
    <section className={styles.workflow}>
      <dl className={styles.workflowFacts}>
        {stage ? (
          <>
            <dt>Stage</dt>
            <dd>{STAGE_LABEL[stage]}</dd>
          </>
        ) : null}
        {priority ? (
          <>
            <dt>Priority</dt>
            <dd>{PRIORITY_LABEL[priority]}</dd>
          </>
        ) : null}
      </dl>
    </section>
  )
}
